import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRedirectResult } from 'firebase/auth';
import { auth, tenantAuth } from '../../config/firebase';
import { API_BASE_URL } from '../../config/api';
import { useToast } from '../ui/Toaster';

// Consume redirect result only once per page load (survives React Strict Mode double-mount)
let redirectResultConsumed = false;

/** Paths where we should run tenant flow when we only have currentUser (no redirect). */
const TENANT_PATHS = ['/tenant', '/deposit-payment'];

function isTenantPath(pathname: string): boolean {
  return TENANT_PATHS.some((p) => pathname.startsWith(p));
}

/**
 * Runs on every app load. Tenant Google sign-in uses the MAIN auth (auth) so the
 * redirect result is never lost (Firebase often applies redirect to the default
 * auth instance). We try getRedirectResult(auth) first, then tenantAuth, then
 * currentUser after a delay only when on a tenant path. Then check-email → update-firebase-uid → portal → navigate.
 * On landlord routes (e.g. /login) we do NOT use currentUser fallback, so a landlord signed in
 * in the same tab won't trigger tenant check-email.
 */
export function TenantRedirectHandler({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current || redirectResultConsumed) return;
    didRun.current = true;

    const runTenantFlow = async (user: { email: string | null; uid: string; getIdToken: () => Promise<string> }) => {
      const email = user.email?.trim();
      if (!email) return false;
      const token = await user.getIdToken();
      const checkUrl = `${API_BASE_URL}/tenants/check-email/${encodeURIComponent(email.toLowerCase())}`;
      const response = await fetch(checkUrl, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 404) {
          await Promise.all([tenantAuth.signOut(), auth.signOut()]).catch(() => {});
          toast.error('Account Not Found', {
            description: 'This email is not registered as a tenant. Please contact your landlord.',
          });
        }
        return false;
      }

      const tenantData = await response.json();
      if (tenantData.needsDeposit) {
        toast.error('Deposit required', {
          description: tenantData.message || 'Please complete your deposit payment first.',
        });
        if (tenantData.depositPaymentToken) {
          navigate(`/deposit-payment/${tenantData.depositPaymentToken}`);
        }
        return false;
      }

      // Link this Firebase UID to the tenant (creates the link if none yet). Same button flow: check email, check deposit, then assign UID.
      const updateResponse = await fetch(`${API_BASE_URL}/tenants/update-firebase-uid`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: tenantData.id, firebaseUid: user.uid }),
      });

      if (!updateResponse.ok) {
        const err = await updateResponse.json().catch(() => ({}));
        console.error('TenantRedirectHandler: update-firebase-uid failed', updateResponse.status, err);
        toast.error('Sign-in failed', {
          description: (err as { message?: string })?.message || 'Could not link your account. Please try again.',
        });
        return false;
      }
      const fullResponse = await fetch(`${API_BASE_URL}/tenants/portal/${tenantData.id}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!fullResponse.ok) {
        console.error('TenantRedirectHandler: portal failed', fullResponse.status);
        toast.error('Sign-in failed', { description: 'Could not load your portal. Please try again.' });
        return false;
      }

      const fullTenantData = await fullResponse.json();
      const tenantId = fullTenantData.id ?? tenantData.id;
      navigate(`/tenant/portal/${tenantId}`, { state: { tenant: fullTenantData }, replace: true });
      return true;
    };

    (async () => {
      try {
        if (redirectResultConsumed) return;
        await new Promise((r) => setTimeout(r, 100));
        if (redirectResultConsumed) return;

        const pathname = location.pathname;
        let user = (await getRedirectResult(auth))?.user ?? null;
        if (!user?.email) user = (await getRedirectResult(tenantAuth))?.user ?? null;

        if (!user?.email) {
          await new Promise((r) => setTimeout(r, 1200));
          user = auth.currentUser ?? tenantAuth.currentUser ?? null;
          // Only use currentUser fallback on tenant paths; on landlord login/dashboard
          // this user may be a landlord, so don't call tenant check-email
          if (user?.email && !isTenantPath(pathname)) return;
        }
        if (!user?.email) return;

        // Before running tenant flow, check if user is authenticated as a landlord
        // If they are, don't run tenant flow (prevents landlords on tenant pages from being logged out)
        try {
          const token = await user.getIdToken();
          const landlordCheckResponse = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          // If user is authenticated as landlord (200 response), don't run tenant flow
          if (landlordCheckResponse.ok) {
            if (process.env.NODE_ENV === 'development') {
              console.log('TenantRedirectHandler: User is authenticated as landlord, skipping tenant flow');
            }
            return;
          }
        } catch (e) {
          // If /auth/me fails (401/403), user is not a landlord, so proceed with tenant flow
          if (process.env.NODE_ENV === 'development') {
            console.log('TenantRedirectHandler: User is not a landlord, proceeding with tenant flow');
          }
        }

        redirectResultConsumed = true;
        await runTenantFlow(user as { email: string | null; uid: string; getIdToken: () => Promise<string> });
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('TenantRedirectHandler:', e);
        toast.error('Sign-in failed', { description: e instanceof Error ? e.message : 'Please try again.' });
      }
    })();
  // location.pathname intentionally omitted: didRun.current already ensures
  // the async logic runs exactly once per page load regardless of navigation.
  }, [navigate, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>;
}
